/**
 * Reward API Service
 * 封装会员积分相关的API调用
 */
(function () {
  'use strict';

  // 检测是否为UAT环境
  const isUATEnvironment = window.location.hostname.includes('uat') ||
    window.location.hostname.includes('myshopify.com');

  const environmentApiBaseUrl = window.themeVariables?.settings?.environmentApiBaseUrl || 'https://website-api-uat.dreo-tech-uat.com/api';
  const environmentTokenKey = window.themeVariables?.settings?.environmentTokenKey || '__us-east-2__auth_token__';

  const API_CONFIG = {
    BASE_URL: environmentApiBaseUrl,
    AUTH_BASE_URL: environmentApiBaseUrl,
    AUTH_TOKEN_KEY: environmentTokenKey,
    TIMEOUT: 30000,
    // UAT环境不设置domain，使用当前域名；正式环境使用.dreo.com
    COOKIE_DOMAIN: isUATEnvironment ? undefined : '.dreo.com',
  };

  /**
   * API服务类
   */
  class RewardApiService {
    constructor() {
      this.baseURL = API_CONFIG.BASE_URL;
      this.timeout = API_CONFIG.TIMEOUT;
    }

    /**
     * 清除所有登录态（Shopify + 自建站）
     */
    clearAllAuth() {
      // 1. 清除自建站 token
      if (typeof Cookies !== 'undefined') {
        const removeOptions = API_CONFIG.COOKIE_DOMAIN ? { domain: API_CONFIG.COOKIE_DOMAIN } : {};
        Cookies.remove(API_CONFIG.AUTH_TOKEN_KEY, removeOptions);
      } else {
        try {
          localStorage.removeItem(API_CONFIG.AUTH_TOKEN_KEY);
        } catch (e) {
          console.warn('Failed to remove token from localStorage:', e);
        }
      }

      // 2. 清除 Shopify 登录态 Cookie（尽力而为，无法完全清除服务端session）
      // Shopify 的 session cookie 是 httpOnly，前端无法直接删除，但可以清除一些辅助cookie
      try {
        const shopifyCookies = ['cart_currency', '_shopify_y', '_shopify_s', '_y'];
        shopifyCookies.forEach(cookieName => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
        });
      } catch (e) {
        console.warn('Failed to clear Shopify cookies:', e);
      }

      console.log('All auth tokens cleared');
    }

    /**
     * 跳转到登录页，并记录当前页面用于登录后重定向
     */
    redirectToLogin() {
      // 如果在 Shopify 后台预览环境，禁止跳转
      if (window.Shopify && window.Shopify.designMode) {
        console.warn('[RewardApiService] Redirect to login blocked in Shopify Design Mode');
        return;
      }

      // 先清除所有登录态
      this.clearAllAuth();

      const currentUrl = window.location.href;
      // 跳转到自建站登录页
      const targetDomain = window.themeVariables?.settings?.targetDomain || 'https://account.dreo.com';
      const loginUrl = `${targetDomain}/en/account?redirectTo=${encodeURIComponent(currentUrl)}&country=US&tab=login`;
      console.log('Redirecting to login page:', loginUrl);
      window.location.href = loginUrl;
    }

    /**
     * 检查Shopify登录状态
     */
    checkShopifyAuth() {
      // 检查Shopify的登录Cookie（更可靠）
      const shopifyCookies = document.cookie.split('; ');
      const hasShopifySession = shopifyCookies.some(cookie =>
        cookie.startsWith('_secure_session_id=') ||
        cookie.startsWith('cart_currency=')
      );

      // 也检查全局变量作为兜底
      const hasCustomerEmail = window.Shopify && window.Shopify.customerEmail;

      const isShopifyLoggedIn = hasShopifySession && hasCustomerEmail;

      // 如果Shopify未登录，跳转登录
      if (!isShopifyLoggedIn) {
        console.log('Shopify customer not logged in, redirecting to login...');
        this.redirectToLogin();
        return false;
      }

      return true;
    }


    /**
     * 获取认证token
     * 从存储中获取
     */
    getAuthToken() {
      return this.getStoredToken();
    }

    /**
     * 从存储中获取token
     */
    getStoredToken() {
      // 优先使用 js-cookie 库
      if (typeof Cookies !== 'undefined') {
        return Cookies.get(API_CONFIG.AUTH_TOKEN_KEY);
      }
      // js-cookie 未加载时，使用原生方式读取 cookie
      try {
        const cookieValue = document.cookie
          .split('; ')
          .find(row => row.startsWith(API_CONFIG.AUTH_TOKEN_KEY + '='));
        if (cookieValue) {
          return decodeURIComponent(cookieValue.split('=')[1]);
        }
      } catch (e) {
        // 忽略 cookie 读取错误
      }
      // 最后尝试 localStorage
      try {
        return localStorage.getItem(API_CONFIG.AUTH_TOKEN_KEY);
      } catch (e) {
        return null;
      }
    }

    /**
     * 存储token
     */
    setStoredToken(token) {
      if (typeof Cookies !== 'undefined') {
        const cookieOptions = { expires: 7 };
        // 只在有COOKIE_DOMAIN配置时才设置domain
        if (API_CONFIG.COOKIE_DOMAIN) {
          cookieOptions.domain = API_CONFIG.COOKIE_DOMAIN;
        }
        Cookies.set(API_CONFIG.AUTH_TOKEN_KEY, token, cookieOptions);
      } else {
        try {
          localStorage.setItem(API_CONFIG.AUTH_TOKEN_KEY, token);
        } catch (e) {
          console.warn('Failed to store token:', e);
        }
      }
    }

    /**
     * 构建请求头
     * @param {object} customHeaders - 自定义请求头
     * @param {boolean} requireAuth - 是否需要认证（默认true）
     */
    async buildHeaders(customHeaders = {}, requireAuth = true) {
      const headers = {
        'Content-Type': 'application/json',
        'hs-user-agent': 'website/2.5.0',
        ...customHeaders
      };

      // 如果需要认证，添加token
      if (requireAuth) {
        const token = await this.getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      return headers;
    }

    /**
     * 处理响应
     */
    async handleResponse(response, endpoint, method) {
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));

        // 埋点：API请求失败
        this.trackApiEvent('api_error', {
          endpoint: endpoint,
          method: method,
          status: response.status,
          error: error.message || 'Request failed'
        });

        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // 埋点：API请求成功
      this.trackApiEvent('api_success', {
        endpoint: endpoint,
        method: method,
        status: response.status
      });

      return data;
    }

    /**
     * 埋点：记录API事件
     */
    trackApiEvent(eventName, eventData = {}) {
      const trackingData = {
        event: `reward_api_${eventName}`,
        timestamp: Date.now(),
        url: window.location.href,
        ...eventData
      };

      // Google Analytics / Google Tag Manager
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(trackingData);
      }

      // Facebook Pixel (可选)
      if (typeof window !== 'undefined' && window.fbq && eventName === 'api_success') {
        // 只在成功时发送Facebook事件，避免过多事件
        const fbEventName = this.getFbEventName(eventData.endpoint, eventData.method);
        if (fbEventName) {
          window.fbq('track', fbEventName, {
            content_name: eventData.endpoint,
            content_type: 'api_call'
          });
        }
      }

      // Shopify Analytics (可选)
      if (typeof window !== 'undefined' && window.Shopify && window.Shopify.analytics) {
        window.Shopify.analytics.publish('reward_api_event', trackingData);
      }
    }

    /**
     * 获取Facebook事件名称
     */
    getFbEventName(endpoint, method) {
      // 根据endpoint和method返回对应的Facebook事件名称
      if (endpoint.includes('/points/balance') || endpoint.includes('/points')) {
        return 'ViewContent';
      }
      if (endpoint.includes('/redeem') && method === 'POST') {
        return 'InitiateCheckout';
      }
      return null;
    }

    /**
     * 带超时的fetch请求
     */
    async fetchWithTimeout(url, options, timeout = this.timeout) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }
        throw error;
      }
    }

    /**
     * GET请求
     * @param {string} endpoint - API端点（如 '/points/balance'）
     * @param {object} params - 查询参数
     * @param {object} options - 额外选项（headers等）
     */
    async get(endpoint, params = {}, options = {}) {
      try {
        // 检查是否是不需要认证的接口（白名单接口，如 /unlogin/ 相关）
        const requireAuth = !endpoint.includes('/unlogin/');

        // 如果需要认证，先检查Shopify登录状态
        if (requireAuth && !this.checkShopifyAuth()) {
          return new Promise(() => { }); // 永远不resolve，因为页面会跳转
        }

        const url = new URL(`${this.baseURL}${endpoint}`);

        // 添加查询参数
        Object.keys(params).forEach(key => {
          if (params[key] !== null && params[key] !== undefined) {
            url.searchParams.append(key, params[key]);
          }
        });

        const headers = await this.buildHeaders(options.headers || {}, requireAuth);

        const response = await this.fetchWithTimeout(url.toString(), {
          method: 'GET',
          headers: headers,
          ...options
        });

        // 先解析响应体，检查是否是401错误（只有需要认证的接口才处理）
        if (requireAuth) {
          try {
            const result = await response.clone().json();

            // 检查响应体中的 code 字段
            if (result.code === 401) {
              console.log('401 error in response body, redirecting to login...');
              // 跳转到登录页（会自动清除所有登录态）
              this.redirectToLogin();
              // 阻止后续代码执行
              return new Promise(() => { }); // 永远不resolve，因为页面会跳转
            }
          } catch (e) {
            // 如果解析 JSON 失败，继续正常处理
            console.log('Failed to parse response as JSON, continuing normal flow');
          }
        }

        return await this.handleResponse(response, endpoint, 'GET');
      } catch (error) {
        console.error('API GET Error:', error);
        throw error;
      }
    }

    /**
     * POST请求
     * @param {string} endpoint - API端点
     * @param {object} data - 请求体数据
     * @param {object} options - 额外选项
     */
    async post(endpoint, data = {}, options = {}) {
      try {
        // 检查Shopify登录状态
        if (!this.checkShopifyAuth()) {
          return new Promise(() => { }); // 永远不resolve，因为页面会跳转
        }

        const url = `${this.baseURL}${endpoint}`;
        const headers = await this.buildHeaders(options.headers || {});

        // 埋点:API请求开始
        this.trackApiEvent('api_request', {
          endpoint: endpoint,
          method: 'POST',
          has_data: !!data && Object.keys(data).length > 0
        });

        const response = await this.fetchWithTimeout(url, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(data),
          ...options
        });

        // 先解析响应体，检查是否是401错误
        try {
          const result = await response.clone().json();

          // 检查响应体中的 code 字段
          if (result.code === 401) {
            console.log('401 error in response body, redirecting to login...');
            // 跳转到登录页（会自动清除所有登录态）
            this.redirectToLogin();
            // 阻止后续代码执行
            return new Promise(() => { }); // 永远不resolve，因为页面会跳转
          }
        } catch (e) {
          // 如果解析 JSON 失败，继续正常处理
          console.log('Failed to parse response as JSON, continuing normal flow');
        }

        return await this.handleResponse(response, endpoint, 'POST');
      } catch (error) {
        console.error('API POST Error:', error);
        throw error;
      }
    }

    /**
     * PUT请求
     * @param {string} endpoint - API端点
     * @param {object} data - 请求体数据
     * @param {object} options - 额外选项
     */
    async put(endpoint, data = {}, options = {}) {
      try {
        // 检查Shopify登录状态
        if (!this.checkShopifyAuth()) {
          return new Promise(() => { }); // 永远不resolve，因为页面会跳转
        }

        const url = `${this.baseURL}${endpoint}`;
        const headers = await this.buildHeaders(options.headers || {});

        // 埋点：API请求开始
        this.trackApiEvent('api_request', {
          endpoint: endpoint,
          method: 'PUT',
          has_data: !!data && Object.keys(data).length > 0
        });

        const response = await this.fetchWithTimeout(url, {
          method: 'PUT',
          headers: headers,
          body: JSON.stringify(data),
          ...options
        });

        // 检查是否是401错误
        if (response.status === 401) {
          console.log('401 error, redirecting to login...');
          // 跳转到登录页（会自动清除所有登录态）
          this.redirectToLogin();
          // 阻止后续代码执行
          return new Promise(() => { }); // 永远不resolve，因为页面会跳转
        }

        return await this.handleResponse(response, endpoint, 'PUT');
      } catch (error) {
        console.error('API PUT Error:', error);
        throw error;
      }
    }

    /**
     * DELETE请求
     * @param {string} endpoint - API端点
     * @param {object} params - 查询参数
     * @param {object} options - 额外选项
     */
    async delete(endpoint, params = {}, options = {}) {
      try {
        // 检查Shopify登录状态
        if (!this.checkShopifyAuth()) {
          return new Promise(() => { }); // 永远不resolve，因为页面会跳转
        }

        const url = new URL(`${this.baseURL}${endpoint}`);
        Object.keys(params).forEach(key => {
          if (params[key] !== null && params[key] !== undefined) {
            url.searchParams.append(key, params[key]);
          }
        });

        const headers = await this.buildHeaders(options.headers || {});

        // 埋点：API请求开始
        this.trackApiEvent('api_request', {
          endpoint: endpoint,
          method: 'DELETE'
        });

        const response = await this.fetchWithTimeout(url.toString(), {
          method: 'DELETE',
          headers: headers,
          ...options
        });

        // 检查是否是401错误
        if (response.status === 401) {
          console.log('401 error, redirecting to login...');
          // 跳转到登录页（会自动清除所有登录态）
          this.redirectToLogin();
          // 阻止后续代码执行
          return new Promise(() => { }); // 永远不resolve，因为页面会跳转
        }

        return await this.handleResponse(response, endpoint, 'DELETE');
      } catch (error) {
        console.error('API DELETE Error:', error);
        throw error;
      }
    }
  }

  // 导出单例实例
  const rewardApiService = new RewardApiService();

  window.RewardApiService = rewardApiService;
})();

